/**
 * @license
 * Copyright 2022 Superflow.dev
 * SPDX-License-Identifier: MIT
 */

import { LitElement, html, css, PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/*

Modes: multiselect, select
Flows: view,edit

*/

/**
 * SfMultiselectSearch element.
 * @fires renderComplete - When the list is populated
 * @fires valueChanged - When the value is changed
 * @property apiId - backend api id
 * @property label - input label
 * @property name - name of the input
 * @property mode - mode of operation
 * @property selectedId - id to preselect
 * @property selectedValue - callback function
 */
@customElement('sf-multiselect-search')
export class SfMultiselectSearch extends LitElement {
  static override styles = css`
    :root {
      --color: #fcc419;
      --shadow: #f08c00;
      --glare: hsl(0 0% 100% / 0.75);
      --font-size: clamp(.75rem, 2vw, 1rem);
      --transition: 0.2s;
    }

    * {
      box-sizing: border-box;
    }

    .d-flex {
      display: flex;
    }

    .flex-col {
      flex-direction: column;
    }

    .flex-wrap {
      flex-wrap: wrap;
    }

    .justify-center {
      justify-content: center;
    }

    .justify-between {
      justify-content: space-between;
    }

    .justify-end {
      justify-content: flex-end;
    }

    .w-100-m-0 {
      width: 100%;
    }
    
    .mb-20 {
      margin-bottom: 20px;
    }
    
    .mt-20 {
      margin-top: 20px;
    }
    .mr-10 {
      margin-right: 10px;
    }
    .mr-5 {
      margin-right: 5px;
    }
    .m-0 {
      margin: 0px;
    }

    .m-20 {
      margin: 20px;
    }
    .md-20-ml-10 {
      margin-top: 20px;
      margin-bottom: 20px;
      margin-left: 10px;
      margin-right: 10px;
    }
    .mrl-5 {
      margin-left: 5px;
      margin-right: 5px;
    }
    .mrl-10 {
      margin-left: 10px;
      margin-right: 10px;
    }
    .mt-10{
      margin-top: 10px
    }
    .ml-10{
      margin-left: 10px
    }
    .mr-20{
      margin-right: 20px
    }
    .flex-grow {
      flex-grow: 1;
    }
    .align-start {
      align-items: flex-start;
    }

    .align-end {
      align-items: flex-end;
    }

    .align-center {
      align-items: center;
    }
    .align-stretch {
      align-items: stretch;
    }
    .align-self-start {
      align-self: flex-start;
    }
    body {
      display: grid;
      place-items: center;
      min-height: 100vh;
      font-family: 'Google Sans', sans-serif, system-ui;
    }
    .hide {
      display: none;
    }
  `;

  @property({ type: String })
  // mode = ""
  mode = "admin"

  @property({ type: Boolean })
  readonly = false
  // readonly = true
  @property()
  listname = ""

  @property({ type: Array })
  listelements: { name: string, id: string }[] = []

  @property({ type: Object })
  listselection: { [key: string]: boolean } = {}

  selectedValues = () => {
    console.log('multiselect selectedvalues', JSON.stringify(this.listselection))
    let retObj: { [key: string]: boolean } = {}
    for (let key of this.listelements) {
      if (this.listselection[key.id] != null && this.listselection[key.id] !== false) {
        retObj[key.id] = this.listselection[key.id]
      }
    }
    return retObj
  }
  selectedValuesNames = () => {
    console.log('multiselect selectedvalues', JSON.stringify(this.listselection))
    let retObj: { [key: string]: any } = {}
    for (let key of this.listelements) {
      if (this.listselection[key.id] != null && this.listselection[key.id] !== false) {
        retObj[key.id] = { name: key.name, id: key.id }
      }
    }
    return retObj
  }
  dispatchValueChanged = () => {
    console.log("dispatchValueChanged");
    this.populateSelectedList();
    let customEvent = new CustomEvent('valueChanged', {
      detail: { source: 'InnerElement', id: this.id },
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(customEvent);
    console.log("dispatched valueChanged event", this.selectedValues());
  }

  initListeners = () => {
    let dropDownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
    dropDownButton?.addEventListener('click', (e) => {
      let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
      let selectedList = this.shadowRoot?.getElementById("selected-list") as HTMLDivElement;
      let button = e.target as HTMLButtonElement;
      if (button.innerHTML === "keyboard_arrow_down") {
        multiselectList.classList.remove("hide");
        selectedList.classList.add("hide");
        button.innerHTML = "keyboard_arrow_up"
      } else {
        multiselectList.classList.add("hide");
        selectedList.classList.remove("hide");
        button.innerHTML = "keyboard_arrow_down"
      }

    })

    let searchInput = this.shadowRoot?.getElementById("search-input") as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.populateList();
      let searchInput = e.target as HTMLInputElement;
      let filterText = searchInput.value.toLowerCase();
      if (filterText !== "") {
        let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
        multiselectList.classList.remove("hide");
        let dropdownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
        dropdownButton.innerHTML = "keyboard_arrow_up"
      }
    })

    let applyButton = this.shadowRoot?.getElementById("apply-button") as HTMLButtonElement;
    applyButton?.addEventListener('click', () => {
      this.populateList(false);
      this.dispatchValueChanged();
      let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
      let selectedList = this.shadowRoot?.getElementById("selected-list") as HTMLDivElement;
      let dropDownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
      if (dropDownButton.innerHTML != "keyboard_arrow_down") {
        multiselectList.classList.add("hide");
        selectedList.classList.remove("hide");
        dropDownButton.innerHTML = "keyboard_arrow_down"
      }
    })
  }

  populateSelectedList = () => {

    let searchInput = this.shadowRoot?.getElementById("search-input") as HTMLInputElement;
    let filterText = (searchInput?.value ?? "").toLowerCase();
    console.log("populateSelectedList", this.listelements, filterText);
    let selectedList = this.shadowRoot?.getElementById("selected-list") as HTMLDivElement;
    let innerHTML = ''
    if (Array.isArray(this.listelements)) {
      this.listelements.map((item) => {
        console.log('sf-multiselect-searcht rendering checkbox', this.listselection[(item.id ?? "")])
        if (this.listselection[(item.id ?? "")] !== true) {
          return;
        }
        innerHTML += `
        <div part="selected-item" class="d-flex align-center mr-10">
          <span id="selected-delete-${(item.id ?? "").replace(/ /g, '-')}" class="material-icons" part="selected-unselect">cancel</span>
          <label part="selected-label" id="selected-label-${(item.id ?? "").replace(/ /g, '-')}">${(item.name ?? "")}</label>
        </div>
      `});
    }
    if (selectedList == null) { return; }
    selectedList.innerHTML = innerHTML;
    for (const item of this.listelements) {
      const deleteSpan = this.shadowRoot?.getElementById(`selected-delete-${(item.id ?? "").replace(/ /g, '-')}`) as HTMLSpanElement;
      if (deleteSpan != null) {
        deleteSpan.addEventListener('click', () => {
          const id = item.id ?? "";
          delete this.listselection[id];
          this.populateList(false);
          this.dispatchValueChanged();
        });
      }
      const selectedLabel = this.shadowRoot?.getElementById(`selected-label-${(item.id ?? "").replace(/ /g, '-')}`) as HTMLLabelElement;
      if (selectedLabel != null) {
        selectedLabel.addEventListener('click', () => {
          const id = item.id ?? "";
          delete this.listselection[id];
          this.populateList(false);
          this.dispatchValueChanged();
        });
      }
    }
  }

  populateList = (applyFlag: boolean = true) => {

    let searchInput = this.shadowRoot?.getElementById("search-input") as HTMLInputElement;
    let filterText = (searchInput?.value ?? "").toLowerCase();
    console.log("populateList", this.listelements, filterText, this.listselection);
    let multiselectList = this.shadowRoot?.getElementById("select-list") as HTMLDivElement;
    let innerHTML = ''
    if (Array.isArray(this.listelements)) {
      if (this.listelements.length > 0) {
        let flagListSelection = false
        this.listelements.map((item) => {
          if (this.listselection[item.id] == true) {
            flagListSelection = true
          }
        })
        innerHTML += `
        <div part="multiselect-item-select-all" class="d-flex">
          <input part="multiselect-checkbox" type="checkbox" id="select-all-checkbox" name="Select All" ${flagListSelection ? "checked" : ""} ${this.readonly ? "disabled" : ""}/>
          <label part="multiselect-checkbox-select-all-label" for="select-all-checkbox">Select All</label>
        </div>
        `
      }
      this.listelements.map((item) => {
        console.log('sf-multiselect-searcht rendering checkbox', this.listselection[(item.id ?? "")])
        if (filterText !== "" && !(item.name ?? "").toLowerCase().includes(filterText)) {
          return;
        }
        innerHTML += `
        <div part="multiselect-item" class="d-flex">
          <input part="multiselect-checkbox" type="checkbox" id="checkbox-${(item.id ?? "").replace(/ /g, '-')}" name="${(item.name ?? "")}" value="${(item.id ?? "")}" ${(this.listselection[(item.id ?? "")] ?? false) ? "checked" : ""} ${this.readonly ? "disabled" : ""}/>
          <label part="multiselect-checkbox-label" for="checkbox-${(item.id ?? "").replace(/ /g, '-')}">${(item.name ?? "")}</label>
        </div>
      `});
    }
    if (multiselectList != null) {
      multiselectList.innerHTML = innerHTML;
    }
    const selectAllCheckbox = this.shadowRoot?.getElementById(`select-all-checkbox`) as HTMLInputElement;
    selectAllCheckbox?.addEventListener('change', () => {
      if (selectAllCheckbox.checked) {
        for (const item of this.listelements) {
          this.listselection[item.id] = true;
        }
      } else {
        for (const item of this.listelements) {
          delete this.listselection[item.id];
        }
      }
      console.log('listselection', this.listselection)
      this.populateList(false);
      // if (Object.keys(this.listselection).length > 0) {
      //   let dropDownButton = this.shadowRoot?.getElementById("dropdown-button") as HTMLButtonElement;
      //   if (dropDownButton.innerHTML != "keyboard_arrow_down") {
      //     multiselectList.classList.add("hide");
      //     dropDownButton.innerHTML = "keyboard_arrow_down"
      //   }
      //   this.dispatchValueChanged();
      // }
    })
    for (const item of this.listelements) {
      const checkbox = this.shadowRoot?.getElementById(`checkbox-${(item.id ?? "").replace(/ /g, '-')}`) as HTMLInputElement;
      if (checkbox) {
        checkbox.addEventListener('change', () => {
          const id = item.id ?? "";
          this.listselection = {
            ...this.listselection,
            [id]: checkbox.checked
          };
        });
      }
    }

    if (Object.keys(this.listselection).length > 0 && applyFlag) {
      this.dispatchValueChanged()
    }
  }

  loadMode = async () => {
    console.log("loadMode", this.mode, this.listelements, this.listselection);
    this.initListeners()
    this.populateList()
  }

  protected override firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {

    this.loadMode();

  }

  override connectedCallback() {
    super.connectedCallback()
  }
  override render() {
    return html`
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@48,400,0,0" />
      <div part="multiselect-container" class="d-flex flex-col align-stretch">  
        ${this.listname != "" ? `<h2 part="multiselect-title">${this.listname}</h2>` : ''}
        <div id="multiselect-input-container" part="multiselect-input-container" class="d-flex">
          <input part="input" type="text" id="search-input" name="search-input" class="flex-grow mr-5" value="" placeholder="Search"/>
          <button id="dropdown-button" part="button-icon-light" class="material-icons mr-5">keyboard_arrow_down</button>
          <button id="apply-button" part="button-icon" class="material-icons">done</button>
        </div>
        <div id="select-list" part="select-list" class="hide d-flex flex-col flex-wrap justify-center"></div>
      </div>
      <div id="selected-list" part="selected-list" class="d-flex flex-wrap"></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
  }
}
